export type CaseStudy = {
  num: string;
  domain: string;
  /** The constraint. What made this hard, in the business's terms. */
  problem: string;
  /** The call you made. This is the part that reads as senior. */
  call: string;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    num: "01",
    domain: "Fintech",
    problem:
      "Remittances is an international, high-volume business — the software had to move money reliably enough that someone waiting on the other side could count on it landing.",
    call:
      "Led the development team end to end: we designed the solutions together and shipped them to production under rigorous quality controls, not just my sign-off."
  },
  {
    num: "02",
    domain: "Auto insurance",
    problem:
      "Money laundering and fraud hide in transaction patterns you can't see one transaction at a time.",
    call:
      "Led the team that built a new microservice for fraud and AML detection, anchored on a unified customer database so transaction monitoring had one source of truth."
  },
  {
    num: "03",
    domain: "Startups",
    problem:
      "Startup-stage systems with opposite but equally unforgiving failure modes: infrastructure that fails silently, and billing that can't fail at all.",
    call:
      "Built device monitoring that anticipated database failures and triggered automated mitigation, and — on a subscription/billing system — treated a duplicate charge as a bug class to eliminate, not tolerate."
  },
  {
    num: "04",
    domain: "Hospitality",
    problem:
      "Customer loyalty lives or dies on whether the rewards system is trustworthy and whether people actually hear about the offer.",
    call:
      "Owned the loyalty rewards system and the mass notification campaigns behind it, so points stayed accurate and offers reached customers without becoming spam."
  },
  {
    num: "05",
    domain: "Healthcare",
    problem:
      "A patient's journey from ambulance pickup to hospital discharge crosses more handoffs than any single system wants to admit.",
    call:
      "Built the tracking system that followed a patient end to end — transport, admission, in-hospital status and procedures, through to discharge — so no handoff lost the thread."
  }
];

export type Project = {
  name: string;
  status: string;
  description: string;
  stack: string[];
  url: string;
};

export const PROJECTS: Project[] = [
  {
    name: "plate-to-plate",
    status: "Live in production",
    description:
      "Meal planning and shared grocery lists, with an in-app AI assistant that generates and adapts plans through typed tool calls.",
    stack: ["Next.js", "Supabase", "AI SDK", "Anthropic", "OpenAI"],
    url: "https://plate-to-plate.madoms.com/"
  }
];

export const GARAGE: { category: string; items: string[] }[] = [
  { category: "Languages", items: ["Java", "JavaScript", "TypeScript", "Python", "SQL"] },
  { category: "Frameworks", items: ["Spring", "Hibernate", "JUnit", "React", "Next.js", "Node.js", "Bootstrap"] },
  { category: "Data", items: ["PostgreSQL", "MongoDB", "MySQL", "Oracle", "Supabase"] },
  { category: "Platform", items: ["AWS", "Docker", "Kubernetes", "Jenkins", "Git", "Linux"] },
  { category: "AI", items: ["Anthropic", "OpenAI", "AI SDK"] }
];

export const LINKEDIN_URL =
  import.meta.env.VITE_LINKEDIN_URL ||
  "https://mx.linkedin.com/in/mario-alberto-dominguez-castillo-a64b3612b";

export type Certification = {
  name: string;
  issuer: string;
  year: string;
  /** Public Credly credential URL. Omit if the credential isn't verifiable online. */
  credentialUrl?: string;
  /** Badge image path under /public (e.g. "/certs/aws-certified-developer-associate.png"). */
  image?: string;
};

function parseCertifications(raw: string | undefined): Certification[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Certification[]) : [];
  } catch {
    console.warn("VITE_CERTIFICATIONS is not valid JSON — rendering no certifications");
    return [];
  }
}

export const CERTIFICATIONS: Certification[] = parseCertifications(
  import.meta.env.VITE_CERTIFICATIONS
);
